"use client";

// ArcPicker — THE colour instrument, everywhere.
//
// One control, one colour model (HSV), one look: concentric arcs that bloom out
// of an orb — hue (outermost) · saturation · brightness · and, where a ground is
// being picked, the gradient bender (innermost). Dragging a knob is live at
// 60fps; the caller decides what "commit" means (persist, POST, nothing).
//
// The only thing that changes between surfaces is ORIENTATION:
//   · "bl" / "br"  — a quarter sweep blooming out of a screen corner (the dock)
//   · "fan"        — a half sweep rising out of a centred orb (panels, sheets)
//   · "left"       — a half sweep opening LEFT out of an orb on its right edge,
//                    for a row in a narrow rail where there is no air above
// Geometry is scale-free: everything derives from one `scale` factor and the
// layout's start/sweep angles, so the same instrument seats itself beautifully
// in a corner, in a settings panel, or in a folder's options.
//
// Callers that speak hex (folder colour, project colour) use <ArcColorPicker>,
// which wraps this with the hex↔HSV plumbing — the old HueDial + sliders
// instrument it replaces had a second, unrelated visual language.

import * as React from "react";
import { createPortal } from "react-dom";
import { hsvToRgb, rgbToHsv, rgbToHex, hexToRgb } from "../ColorInstrument";

/** h · s · v · g(radient) · t(one). `g` is grounds only; `t` is the tone arc. */
export type Chan = "h" | "s" | "v" | "g" | "t";
export type Hsv = { h: number; s: number; v: number };

export type ArcLayout = "bl" | "br" | "fan" | "left";

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
// Arc segments per ring, keyed by CHANNEL — because the cost and the demands are
// not the same for each. Hue is the one people read as a spectrum: at 28 steps
// it visibly bands into stripes, and it is also the ONE ramp that never changes
// (the spectrum is constant, so ArcRing's memo means it renders once and never
// again on a drag). It can afford to be genuinely continuous. Saturation and
// brightness re-mint on every pointer move, so they stay lean — and their ramps
// are near-monochrome, where banding doesn't read anyway.
const SEG_FOR: Record<Chan, number> = { h: 132, s: 40, v: 40, g: 40, t: 44 };
const HALF_PI = Math.PI / 2;

/** where the sweep starts and how far it runs, per layout (0 = due north). */
const SWEEP: Record<ArcLayout, { start: number; sweep: number }> = {
  bl: { start: 0, sweep: HALF_PI },            // north → east, out of the bottom-left
  br: { start: -HALF_PI, sweep: HALF_PI },     // west → north, out of the bottom-right
  fan: { start: -HALF_PI, sweep: Math.PI },    // west → north → east, out of a centred orb
  left: { start: -Math.PI, sweep: Math.PI },   // south → west → north, out of an orb on the right
};

export interface ArcGeom {
  R_HUE: number; R_SAT: number; R_BRI: number; R_GRAD: number;
  KNOB: number; STROKE: number;
  W: number; H: number; CX: number; CY: number;
  ORB: number; ORB_OPEN: number;
  start: number; sweep: number;
}

/** geometry scales with a single factor so the instrument shrinks intact. */
export function arcGeom(k: number, layout: ArcLayout): ArcGeom {
  const R_HUE = 178 * k, R_SAT = 138 * k, R_BRI = 98 * k, R_GRAD = 58 * k;
  const KNOB = 11.5 * k, STROKE = 16 * k;
  const { start, sweep } = SWEEP[layout];
  const PAD = 36 * k;
  const reach = R_HUE + KNOB + 4 * k;
  const ORB = 19 * k, ORB_OPEN = 27 * k;
  if (layout === "fan") {
    // centred orb, arcs rising over it
    return { R_HUE, R_SAT, R_BRI, R_GRAD, KNOB, STROKE, ORB, ORB_OPEN, start, sweep,
      W: reach * 2, H: reach + PAD, CX: reach, CY: reach };
  }
  if (layout === "left") {
    // orb on the right edge, arcs reaching out to the left of it
    return { R_HUE, R_SAT, R_BRI, R_GRAD, KNOB, STROKE, ORB, ORB_OPEN, start, sweep,
      W: reach + PAD, H: reach * 2, CX: reach, CY: reach };
  }
  const W = PAD + reach, H = PAD + reach;
  return { R_HUE, R_SAT, R_BRI, R_GRAD, KNOB, STROKE, ORB, ORB_OPEN, start, sweep,
    W, H, CX: layout === "br" ? W - PAD : PAD, CY: H - PAD };
}

/** the ring stack, innermost-last. The 4th ring is the ground's gradient bender
 *  where a ground is being picked, and otherwise the TONE arc (see `toneAt`). */
export function arcRings(g: ArcGeom, gradient: boolean, tones = false): { ch: Chan; r: number; label: string }[] {
  const rings: { ch: Chan; r: number; label: string }[] = [
    { ch: "h", r: g.R_HUE, label: "hue" },
    { ch: "s", r: g.R_SAT, label: "saturation" },
    { ch: "v", r: g.R_BRI, label: "brightness" },
  ];
  if (gradient) rings.push({ ch: "g", r: g.R_GRAD, label: "gradient" });
  else if (tones) rings.push({ ch: "t", r: g.R_GRAD, label: "tone" });
  return rings;
}

/**
 * THE TONE ARC — the same colour, lighter or darker, as one sweep.
 *
 * Picking a colour is two different questions and they were being answered by
 * the same three rings: *which* colour (hue), and *how light* this one should be.
 * The second is the one people actually redo — a folder is the right blue but too
 * loud, or the right green but too pale — and it used to mean hunting the
 * saturation and brightness rings until the pair looked right again.
 *
 * So the innermost ring holds a run of tones OF THE COLOUR ALREADY PICKED.
 * Centre (0.5) is exactly what you chose. Left of it the colour lifts toward
 * light — brightness up, chroma easing off, the way a wash of the same pigment
 * thins. Right of it, it sinks — brightness down, chroma holding, so a dark tone
 * reads as depth rather than mud. One drag, one axis, and every stop on it is
 * unmistakably still your colour.
 */
export function toneAt(hsv: Hsv, t: number): Hsv {
  const d = (t - 0.5) * 2; // −1 lightest · 0 as picked · +1 darkest
  if (d <= 0) {
    const k = -d;
    return { h: hsv.h, s: hsv.s * (1 - k * 0.62), v: hsv.v + (1 - hsv.v) * k * 0.92 };
  }
  return { h: hsv.h, s: Math.min(1, hsv.s * (1 + d * 0.16)), v: hsv.v * (1 - d * 0.68) };
}

/**
 * The gradient bender's own preview ramp — the run of top-colours the ground
 * sweeps through as the bender opens. Kept here so the arc and the real ground
 * (lib/ground.ts) describe the SAME motion: hue forward, brightness up, chroma
 * eased a touch so it glows instead of screaming.
 */
export function benderAt(hsv: Hsv, t: number): string {
  return rgbToHex(hsvToRgb(hsv.h + t * 42, clamp01(hsv.s * (1 - t * 0.24)), clamp01(hsv.v + t * 0.15)));
}

/**
 * Where a point falls along a ring's sweep, 0..1. `dx`/`dy` are measured from
 * the sweep's centre, in screen axes (y down).
 *
 * MEASURED FROM THE MIDDLE OF THE SWEEP, not from its start. Subtracting the
 * start alone works only while the sweep avoids atan2's ±π seam — the "left"
 * layout sits right on it, and a pointer a hair below due south came back as
 * +π instead of −π and threw the knob to the far end. Re-wrapping around the
 * midpoint puts every layout the same distance from the seam, and a point
 * outside the sweep still clamps to the nearer end rather than flipping.
 */
export function arcT(g: { start: number; sweep: number }, dx: number, dy: number): number {
  const a = Math.atan2(dx, -dy); // 0 up, +π/2 right
  const rel = a - (g.start + g.sweep / 2);
  const wrapped = Math.atan2(Math.sin(rel), Math.cos(rel));
  return clamp01(wrapped / g.sweep + 0.5);
}

export interface ArcPickerProps {
  value: Hsv;
  /** the gradient bender level (0..1). Pass with `gradient` to show its ring. */
  grad?: number;
  /** live, on every pointer move / key nudge. */
  onChange: (next: Hsv, grad: number) => void;
  /** once, on release — persist here, not in onChange. */
  onCommit?: (next: Hsv, grad: number) => void;
  /** called when a drag starts / ends, so the host can go 1:1 (no easing). */
  onLive?: (live: boolean) => void;
  layout?: ArcLayout;
  scale?: number;
  /** show the 4th, innermost ring — the ground gradient bender. */
  gradient?: boolean;
  /** show the TONE arc in the 4th ring instead (ignored when `gradient`). */
  tones?: boolean;
  /** where the tone knob rests, 0..1 — 0.5 is the colour exactly as picked. */
  tone?: number;
  onTone?: (t: number) => void;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * One ring's coloured arc. Split out and memoised because this is where the
 * cost of the instrument lives: every ring is SEG round-capped segments, and a
 * drag re-renders the picker on every pointer move. A ring only takes the
 * channels its own ramp reads, so React's shallow compare skips the rest —
 * the hue spectrum is constant and never re-renders once mounted.
 */
const ArcRing = React.memo(function ArcRing({
  ch, r, cx, cy, start, sweep, h, s, v,
}: {
  ch: Chan; r: number; cx: number; cy: number; start: number; sweep: number;
  h?: number; s?: number; v?: number;
}) {
  const segs: React.JSX.Element[] = [];
  const SEG = SEG_FOR[ch];
  for (let i = 0; i < SEG; i++) {
    const t0 = i / SEG, t1 = (i + 1) / SEG;
    const a0 = start + t0 * sweep, a1 = start + t1 * sweep;
    const x0 = cx + r * Math.sin(a0), y0 = cy - r * Math.cos(a0);
    const x1 = cx + r * Math.sin(a1), y1 = cy - r * Math.cos(a1);
    const t = (t0 + t1) / 2;
    // hue is the pure spectrum; saturation and brightness ride the CURRENT pick
    // so each scale previews the actual result; the bender previews the run of
    // grounds it sweeps through.
    const stroke =
      ch === "h" ? rgbToHex(hsvToRgb(t * 360, 0.9, 1))
      : ch === "s" ? rgbToHex(hsvToRgb(h ?? 0, t, Math.max(v ?? 0, 0.62)))
      : ch === "v" ? rgbToHex(hsvToRgb(h ?? 0, Math.max(s ?? 0, 0.5), t))
      : ch === "t" ? (() => { const q = toneAt({ h: h ?? 0, s: s ?? 0, v: v ?? 0 }, t); return rgbToHex(hsvToRgb(q.h, q.s, q.v)); })()
      : benderAt({ h: h ?? 0, s: s ?? 0, v: v ?? 0 }, t);
    segs.push(
      <path key={i} className="gd-seg" d={`M ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1}`}
        stroke={stroke} strokeLinecap="round" fill="none" />,
    );
  }
  return <>{segs}</>;
});

/**
 * The arcs themselves — an absolutely-positioned SVG that pins its centre to
 * (0,0) of its parent in the corner layouts, and centres itself in the fan.
 */
export default function ArcPicker({
  value, grad = 0, onChange, onCommit, onLive,
  layout = "bl", scale = 1, gradient = false, tones = false, tone = 0.5, onTone, className, style,
}: ArcPickerProps): React.JSX.Element {
  const g = React.useMemo(() => arcGeom(scale, layout), [scale, layout]);
  const svgRef = React.useRef<SVGSVGElement>(null);
  const activeRef = React.useRef<Chan | null>(null);
  const hsvRef = React.useRef(value);
  const gradRef = React.useRef(grad);
  const toneRef = React.useRef(tone);
  React.useEffect(() => { hsvRef.current = value; }, [value]);
  React.useEffect(() => { gradRef.current = grad; }, [grad]);
  React.useEffect(() => { toneRef.current = tone; }, [tone]);

  const pt = React.useCallback((r: number, t: number): [number, number] => {
    const a = g.start + t * g.sweep; // 0 = straight up (north)
    return [g.CX + r * Math.sin(a), g.CY - r * Math.cos(a)];
  }, [g]);

  // pointer angle → normalised value along the sweep (scale-independent).
  const angleT = React.useCallback((clientX: number, clientY: number): number => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const dx = clientX - (rect.left + g.CX);
    const dy = clientY - (rect.top + g.CY);
    return arcT(g, dx, dy);
  }, [g]);

  const setChan = React.useCallback((ch: Chan, t: number) => {
    if (ch === "g") { gradRef.current = t; onChange(hsvRef.current, t); return; }
    if (ch === "t") { toneRef.current = t; onTone?.(t); return; }
    const next = { ...hsvRef.current };
    if (ch === "h") next.h = t * 360;
    else if (ch === "s") next.s = t;
    else next.v = t;
    hsvRef.current = next;
    onChange(next, gradRef.current);
  }, [onChange, onTone]);

  const start = (ch: Chan) => (e: React.PointerEvent) => {
    e.preventDefault();
    activeRef.current = ch;
    onLive?.(true);
    // capture on the actual interactive element (the svg is pointer-events:none);
    // captured events still bubble to the svg's move/up handlers. Auto-releases
    // on pointerup, so no manual release is needed.
    try { (e.currentTarget as Element).setPointerCapture(e.pointerId); } catch { /* older browser */ }
    setChan(ch, angleT(e.clientX, e.clientY));
  };
  const onMove = (e: React.PointerEvent) => {
    if (!activeRef.current) return;
    setChan(activeRef.current, angleT(e.clientX, e.clientY));
  };
  const onUp = () => {
    if (!activeRef.current) return;
    activeRef.current = null;
    onLive?.(false);
    onCommit?.(hsvRef.current, gradRef.current);
  };

  // arrow-key nudges for a knob (a11y) — hue in degrees, s/v/g in 0..1.
  const nudge = (ch: Chan, dir: number) => {
    const cur = hsvRef.current;
    const now = ch === "h" ? cur.h / 360 : ch === "s" ? cur.s : ch === "v" ? cur.v
      : ch === "t" ? toneRef.current : gradRef.current;
    const step = ch === "h" ? 5 / 360 : ch === "g" ? 0.06 : 0.04;
    setChan(ch, clamp01(now + dir * step));
    onCommit?.(hsvRef.current, gradRef.current);
  };

  // colour ramps — hue is the pure spectrum; saturation and brightness ride the
  // CURRENT pick so each scale previews the actual result; the bender previews
  // the run of grounds it sweeps through.
  const valueOf: Record<Chan, number> = { h: value.h / 360, s: value.s, v: value.v, g: grad, t: tone };

  return (
    <svg
      ref={svgRef}
      className={`gd-arcs${className ? ` ${className}` : ""}`}
      width={g.W}
      height={g.H}
      viewBox={`0 0 ${g.W} ${g.H}`}
      style={{ ["--gd-stroke" as string]: `${g.STROKE}px`, ...style }}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      {/* one group per ring: hover thickens the arc + pops its knob (a springy
          bounce). hit = a wide invisible track so a click anywhere on the ring
          jumps its knob. */}
      {arcRings(g, gradient, tones).map(({ ch, r, label }) => {
        const [hx0, hy0] = pt(r, 0);
        const [hx1, hy1] = pt(r, 1);
        const t = valueOf[ch];
        const [kx, ky] = pt(r, t);
        return (
          <g className="gd-ring" key={ch}>
            <path className="gd-hit" d={`M ${hx0} ${hy0} A ${r} ${r} 0 0 1 ${hx1} ${hy1}`}
              stroke="transparent" strokeWidth={g.STROKE + 20} strokeLinecap="round" fill="none"
              onPointerDown={start(ch)} />
            {/* Each ring is memoised on the channels its OWN ramp reads, so a
                drag only rebuilds the arcs whose colours actually changed — the
                hue spectrum never rebuilds at all, and the whole instrument
                stops re-minting ~180 <path>s per pointer move. */}
            <ArcRing ch={ch} r={r} cx={g.CX} cy={g.CY} start={g.start} sweep={g.sweep}
              h={ch === "h" ? undefined : value.h}
              s={ch === "v" || ch === "g" || ch === "t" ? value.s : undefined}
              v={ch === "s" || ch === "g" || ch === "t" ? value.v : undefined} />
            <g className="gd-knob" transform={`translate(${kx} ${ky})`}
              tabIndex={0} role="slider" aria-label={label}
              aria-valuenow={Math.round(ch === "h" ? value.h : t * 100)}
              aria-valuemin={0} aria-valuemax={ch === "h" ? 360 : 100}
              onPointerDown={start(ch)}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight" || e.key === "ArrowUp") { e.preventDefault(); nudge(ch, 1); }
                if (e.key === "ArrowLeft" || e.key === "ArrowDown") { e.preventDefault(); nudge(ch, -1); }
              }}>
              <circle r={g.KNOB} className="gd-knob-disc" />
            </g>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * The orb — the instrument's eye. Closed it IS the current colour; it carries a
 * slight linear gradient of itself so it reads as a lit object rather than a
 * flat dot, and a prominent stroke so it seats on any ground like a chip.
 */
export function ColorOrb({
  color, size, open = false, onClick, label, className, style,
}: {
  color: string;
  size: number;
  open?: boolean;
  onClick?: () => void;
  label?: string;
  className?: string;
  style?: React.CSSProperties;
}): React.JSX.Element {
  const face = orbFace(color);
  const props = {
    className: `gd-dot${open ? " is-open" : ""}${className ? ` ${className}` : ""}`,
    style: { width: size, height: size, backgroundImage: face, ...style },
  };
  if (!onClick) return <span aria-hidden {...props} />;
  return (
    <button type="button" aria-label={label} aria-expanded={open} onClick={onClick} {...props} />
  );
}

/** a soft top-lit wash of the colour itself — never a second, foreign hue. */
export function orbFace(color: string): string {
  return `linear-gradient(160deg, color-mix(in oklab, ${color} 82%, white) 0%, ${color} 52%, color-mix(in oklab, ${color} 88%, black) 100%)`;
}

// ── hex-speaking wrapper — the drop-in for every panel picker ────────────────
/**
 * ArcColorPicker — the instrument for surfaces that store a hex (folder colour,
 * project colour, the settings ground). Same arcs, same knobs, same ramps; the
 * orb sits under the fan showing exactly what will be saved.
 */
export function ArcColorPicker({
  value, onChange, onCommit, layout = "fan", scale = 0.62, gradient = false, grad, onGrad,
  collapsible = false, label = "pick a colour", orbScale = 1, onOpenChange,
  tones = true, escape = false,
}: {
  value: string | null;
  onChange: (hex: string) => void;
  onCommit?: (hex: string) => void;
  layout?: ArcLayout;
  scale?: number;
  gradient?: boolean;
  grad?: number;
  onGrad?: (g: number) => void;
  /** COLLAPSED BY DEFAULT — the instrument rests as a single colour orb and only
   *  blooms into the arcs when it is asked to, then folds back. A panel should
   *  not be dominated by a dial nobody opened. */
  collapsible?: boolean;
  /** accessible name for the closed orb. */
  label?: string;
  /** the resting orb, relative to its default — a row of quick picks wants the
   *  instrument to read as the largest thing in it, not one more swatch. */
  orbScale?: number;
  /** so a host row can stand its other options down while the arcs are open. */
  onOpenChange?: (open: boolean) => void;
  /** the TONE arc in the 4th ring. On by default; pass false where the surface
   *  tunes h · s · l itself and a fourth, softer axis would only muddy what is
   *  being stored (the netti tuner's palette pins). */
  tones?: boolean;
  /** BLOOM OUTSIDE THE PANEL. A scrolling rail clips on BOTH axes (`overflow-y:
   *  auto` computes overflow-x to auto too), so arcs opening inside one are cut
   *  off and half the instrument cannot be reached. With this the open fan is
   *  portalled to the body and fixed to the orb's place on screen, so it reads
   *  over whatever it is standing in front of. */
  escape?: boolean;
}): React.JSX.Element {
  const seed = React.useMemo(() => (value ? rgbToHsv(hexToRgb(value)) : { h: 210, s: 0.5, v: 0.85 }), [value]);
  const [hsv, setHsv] = React.useState<Hsv>({ h: seed.h || 210, s: seed.s, v: seed.v < 0.05 ? 0.85 : seed.v });
  // WHERE THE TONE KNOB RESTS. Dragging it never rewrites `hsv` — the base colour
  // has to survive a trip out to "much lighter" and back, so the tone is a lens
  // over the pick, not a new pick. It re-centres on release: whatever tone you
  // settled on becomes the colour, and the arc is ready to shade THAT.
  const [tone, setTone] = React.useState(0.5);
  const lastEmit = React.useRef<string | null>(null);
  const g = React.useMemo(() => arcGeom(scale, layout), [scale, layout]);

  // re-seed when the parent sets a colour we didn't emit (a quick swatch, a reset)
  React.useEffect(() => {
    if (!value) return;
    if (value.toLowerCase() === lastEmit.current?.toLowerCase()) return; // our own echo
    const s = rgbToHsv(hexToRgb(value));
    setHsv({ h: s.h || 210, s: s.s, v: s.v < 0.05 ? 0.85 : s.v });
  }, [value]);

  const emit = (next: Hsv, cb?: (hex: string) => void) => {
    const hex = rgbToHex(hsvToRgb(next.h, next.s, next.v));
    lastEmit.current = hex;
    cb?.(hex);
    return hex;
  };
  // what the orb WEARS and what gets saved is the base seen through the tone.
  const shown = toneAt(hsv, tone);
  const face = rgbToHex(hsvToRgb(shown.h, shown.s, shown.v));
  const [open, setOpen] = React.useState(false);
  const setOpenAnd = (v: boolean) => { setOpen(v); onOpenChange?.(v); };
  const orbSize = Math.round((38 * scale + 16) * orbScale);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpenAnd(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const arcs = (
    <ArcPicker
      value={hsv}
      grad={grad ?? 0}
      gradient={gradient}
      // the tone arc takes the 4th ring wherever a ground's bender doesn't need it
      tones={tones && !gradient}
      tone={tone}
      onTone={(t) => { setTone(t); emit(toneAt(hsv, t), onChange); }}
      layout={layout}
      scale={scale}
      onChange={(next, gr) => { setHsv(next); emit(toneAt(next, tone), onChange); onGrad?.(gr); }}
      onCommit={(next) => {
        // FOLD THE TONE IN and re-centre the arc. The colour you settled on is now
        // simply the colour; the next drag shades that one, not the one before it.
        const settled = toneAt(next, tone);
        setHsv(settled);
        setTone(0.5);
        emit(settled, onCommit ?? onChange);
      }}
    />
  );

  // ── the resting instrument ─────────────────────────────────────────────────
  // Closed, it is exactly one thing: the colour, as a circle you can press. The
  // arcs bloom ABSOLUTELY out of the orb's centre, so opening the dial never
  // reflows the panel around it — the picker takes the air above itself, then
  // gives it back.
  if (collapsible) {
    return (
      <CollapsedPicker
        g={g}
        layout={layout}
        orbSize={orbSize}
        face={face}
        label={label}
        open={open}
        setOpen={setOpenAnd}
        escape={escape}
      >
        {arcs}
      </CollapsedPicker>
    );
  }

  return (
    <div className="gd-inline" style={{ width: g.W, height: g.H }}>
      {arcs}
      <ColorOrb color={face} size={orbSize} style={{ left: g.CX, top: g.CY }} />
    </div>
  );
}

/**
 * The resting instrument. Closed, it is exactly one thing: the colour, as a
 * circle you can press. The arcs bloom ABSOLUTELY out of the orb's centre, so
 * opening the dial never reflows the panel around it — the picker takes the air
 * it needs, then gives it back.
 *
 * `escape` moves that air OUT of the panel: the bloom is portalled to the body
 * and fixed to wherever the orb is standing, which is the only way an
 * instrument inside a scrolling rail can be whole (a rail clips both axes).
 * It re-measures on scroll and resize, so the arcs stay on their orb.
 */
function CollapsedPicker({
  g, layout, orbSize, face, label, open, setOpen, escape, children,
}: {
  g: ArcGeom;
  layout: ArcLayout;
  orbSize: number;
  face: string;
  label: string;
  open: boolean;
  setOpen: (v: boolean) => void;
  escape: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const hostRef = React.useRef<HTMLDivElement>(null);
  const [at, setAt] = React.useState<{ x: number; y: number } | null>(null);

  // BRING THE ROW TO WHERE THE FAN FITS. Clamping alone keeps every knob
  // reachable, but an orb near the foot of a rail ends up with its arcs floating
  // well above it, reading as if they belong to the row overhead. Centring the
  // row in its scroller first means the bloom almost always lands ON its own
  // orb, and the clamp below goes back to being the rare last resort.
  React.useEffect(() => {
    if (!open || !escape) return;
    const bring = () => hostRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    bring();
    // ONCE MORE WHEN THE PANEL HAS FINISHED OPENING. A pin pressed while its own
    // fold is still expanding is still clipped to no height, so the first ask
    // scrolls nowhere; a beat later the row is real and the ask lands. (The
    // follow loop below keeps the bloom glued to the orb through both.)
    const id = window.setTimeout(bring, 420);
    return () => window.clearTimeout(id);
  }, [open, escape]);

  React.useEffect(() => {
    if (!open || !escape) { setAt(null); return; }
    const place = () => {
      const r = hostRef.current?.getBoundingClientRect();
      if (!r) return;
      // anchored to the orb, then HELD INSIDE THE WINDOW. An orb near the foot
      // of a long rail would otherwise hang half its sweep below the fold, and
      // an arc you cannot reach is not a control. The nudge only happens within
      // a fan's reach of an edge, so in the middle of a panel it never moves.
      const M = 10;
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;
      const maxX = window.innerWidth - M - (g.W - g.CX);
      const maxY = window.innerHeight - M - (g.H - g.CY);
      const next = {
        x: Math.max(M + g.CX, Math.min(x, Math.max(M + g.CX, maxX))),
        y: Math.max(M + g.CY, Math.min(y, Math.max(M + g.CY, maxY))),
      };
      // only when it actually moved — otherwise the follow below would re-render
      // the whole instrument every frame, including mid-drag
      setAt((was) => (was && Math.abs(was.x - next.x) < 0.5 && Math.abs(was.y - next.y) < 0.5 ? was : next));
    };
    place();
    // capture, so a scroll in ANY ancestor moves the bloom with its orb
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    // AND FOLLOW IT WHILE THE ROOM SETTLES. Opening a picker inside a panel that
    // is itself still opening — a fold expanding, a smooth scroll running — moves
    // the orb without firing a scroll event, and the bloom is left behind
    // pointing at the row above. A second of frames covers every settle we have;
    // after that the listeners above are enough.
    let raf = 0;
    const until = performance.now() + 1000;
    const follow = () => {
      place();
      if (performance.now() < until) raf = requestAnimationFrame(follow);
    };
    raf = requestAnimationFrame(follow);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, escape, g]);

  const box: React.CSSProperties = escape
    ? { width: g.W, height: g.H, left: 0, top: 0 }
    : { width: g.W, height: g.H, left: orbSize / 2 - g.CX, top: orbSize / 2 - g.CY };

  // AN OPEN PICKER MUST NOT EAT THE NEXT ONE'S FIRST PRESS. A full-screen scrim
  // is the right way to fold a single instrument back, but a ROW of them (the
  // palette's three pins) turns every move between pins into two clicks: one
  // swallowed by the scrim, one that finally lands. Escaped pickers watch the
  // document instead, so a press on a neighbouring orb closes this one and opens
  // that one in the same gesture.
  React.useEffect(() => {
    if (!open || !escape) return;
    const away = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (hostRef.current?.contains(t)) return;
      if ((t as Element).closest?.(".gd-escape")) return;
      setOpen(false);
    };
    // a frame's grace, so the press that OPENED it is not the press that closes it
    const id = window.setTimeout(() => document.addEventListener("pointerdown", away, true), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("pointerdown", away, true);
    };
  }, [open, escape, setOpen]);

  const bloom = (
    <>
      {/* press anywhere else to fold it back. The escaped picker watches the
          document instead (above) so it never swallows a neighbour's press. */}
      {escape ? null : (
        <button type="button" className="gd-scrim" aria-label="close colour picker" onClick={() => setOpen(false)} />
      )}
      {/* the arcs read against a soft field, never raw panel content */}
      <div className="gd-fanveil" data-layout={layout} aria-hidden style={box}>
        <i /><i /><i />
      </div>
      <div className="gd-bloomwrap" style={box}>{children}</div>
    </>
  );

  return (
    <div ref={hostRef} className="gd-collapse" style={{ width: orbSize, height: orbSize }} data-open={open || undefined}>
      {open && !escape ? bloom : null}
      {open && escape && at
        ? createPortal(
            <div className="gd-escape" style={{ left: at.x - g.CX, top: at.y - g.CY, width: g.W, height: g.H }}>
              {bloom}
            </div>,
            document.body,
          )
        : null}
      <ColorOrb
        color={face}
        size={orbSize}
        open={open}
        onClick={() => setOpen(!open)}
        label={open ? "close colour picker" : label}
      />
    </div>
  );
}
